import { ReactElement } from 'react'
import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import { PriceLabel } from '~/components/elements/PriceLabel'
import { MemberBadge } from '~/features/shared/components/MemberBadge'
import { getCoordinateImageAlt } from '~/features/shared/coordinate/lib/getCoordinateImageAlt'
import { getItemImageAlt } from '~/features/shared/item/lib/getItemImageAlt'

type CommonProps = {
  imagePriority?: boolean
  onClick?: () => void
}

type BaseProps = {
  img: ReactElement
  info: ReactElement
}
const Base: React.FC<BaseProps> = ({ img, info }) => (
  <li className="flex flex-col gap-y-[6px] leading-[normal] xl:gap-y-0">
    <div className="grow overflow-hidden rounded-[3px]">{img}</div>
    {info}
  </li>
)

type CoordinateProps = Schemas.folder_coordinate_detail & CommonProps
const Coordinate: React.FC<CoordinateProps> = ({
  coordinate,
  imagePriority,
  onClick,
}) => (
  <Base
    img={
      <a
        href={`/${coordinate.member.user_name}/${coordinate.id}/`}
        className="block h-full xl:hover:opacity-70"
        onClick={onClick}
      >
        <Img
          src={coordinate.image.url_500}
          alt={getCoordinateImageAlt({
            nickName: coordinate.member.nick_name,
          })}
          className="size-full object-cover"
          width={500}
          height={667}
          priority={imagePriority}
        />
      </a>
    }
    info={
      <a
        href={`/${coordinate.member.user_name}/`}
        className="flex gap-x-1 xl:gap-x-2 xl:pt-[5px] xl:hover:opacity-70"
      >
        <Img
          src={coordinate.member.image.url_80}
          className="size-6 shrink-0 rounded-full border border-gray-300 xl:size-10"
          alt=""
          width={32}
          height={32}
          priority={imagePriority}
        />
        <div className="grow overflow-hidden xl:flex xl:flex-col xl:gap-y-[1px] xl:pt-[1px]">
          <div className="flex items-center gap-x-[3px]">
            <p className="truncate text-[10px] font-bold xl:text-[14px]">
              {coordinate.member.nick_name}
            </p>
            <MemberBadge
              className="inline-block size-[15px] -va-4 xl:size-4 xl:-va-2"
              member={coordinate.member}
            />
          </div>
          {coordinate.member.height !== null && (
            <p className="text-[9px] xl:text-[13px]">
              {coordinate.member.height}cm
            </p>
          )}
        </div>
      </a>
    }
  />
)

type ItemProps = Schemas.folder_item_detail & CommonProps
const Item: React.FC<ItemProps> = ({ item, image, imagePriority, onClick }) => (
  <Base
    img={
      <a
        className="relative block h-full xl:hover:opacity-70"
        href={`/item/${item.id}/`}
        onClick={onClick}
      >
        <Img
          className="size-full object-cover"
          src={image?.url_500 ?? ''}
          alt={getItemImageAlt({
            brandName: item.brand.name,
            brandNameKana: item.brand.name_kana || undefined,
            itemName: item.name,
            categoryName: item.category.name,
          })}
          width={500}
          height={600}
          priority={imagePriority}
        />
        <p className="absolute bottom-[6px] left-[6px] text-[11px] leading-none xl:bottom-2 xl:left-2">
          <PriceLabel size="large" displayPrice={item.display_price} />
        </p>
      </a>
    }
    info={
      <div className="xl:flex xl:h-[45px] xl:flex-col xl:gap-y-[2px] xl:pt-2">
        <p className="truncate text-[10px] font-bold xl:text-[13px]">
          {item.brand.name}
        </p>
        <p className="truncate text-[9px] xl:text-[12px]">
          {item.category.name}
        </p>
      </div>
    }
  />
)

type Props = {
  folderCoordinate: Schemas.folder_coordinate_detail | null
  folderItem: Schemas.folder_item_detail
  imagePriority?: boolean
  onClick?: () => void
}

export const FolderElementListItem: React.FC<Props> = ({
  folderCoordinate,
  folderItem,
  imagePriority,
  onClick,
}) => {
  if (folderCoordinate !== null) {
    return (
      <Coordinate
        {...folderCoordinate}
        imagePriority={imagePriority}
        onClick={onClick}
      />
    )
  }
  if (folderItem !== null) {
    return (
      <Item {...folderItem} imagePriority={imagePriority} onClick={onClick} />
    )
  }
  return null
}
